import {
  ApiDateTime,
  CounterpartyId,
  CreateQuestionnaire,
  CreateQuestionnaireDocumentation,
  DocumentationId,
  FileId,
  ImportQuestions,
  LayerType,
  OwnerId,
  QAMode,
  QuestionnaireId,
  ScopeMatrix,
  SectionId,
  TrustCenterAccess,
  PromptModifiers,
  DealId,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function get(questionnaireId: QuestionnaireId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}`,
    {path: {questionnaire_id: questionnaireId}},
  );
}

export async function list() {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/questionnaires`, {});
}

export async function listHistorical() {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/historical_questionnaires`,
    {},
  );
}

export async function create(questionnaire: CreateQuestionnaire) {
  const res = await platformedClient.internalRequest("post", `/region/{region}/api/vendor_toolkit/questionnaires`, {
    body: questionnaire,
  });
  await invalidateQueries([
    q.vendorToolkit.questionnaires.filter(),
    q.vendorToolkit.tasks.filter(),
    q.vendorToolkit.gettingStartedChecklist.filter(),
  ]);
  return res;
}

export async function assign(questionnaireId: QuestionnaireId, ownerId: OwnerId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/assign`,
    {body: ownerId, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateDueDate(questionnaireId: QuestionnaireId, dueDate: ApiDateTime | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/due_date`,
    {body: dueDate, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateQuestionnaireDate(questionnaireId: QuestionnaireId, questionnaireDate: ApiDateTime | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/questionnaire_date`,
    {body: questionnaireDate, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateName(questionnaireId: QuestionnaireId, name: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/name`,
    {body: name, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateInternalLanguageCode(questionnaireId: QuestionnaireId, languageCode: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/internal_language_code`,
    {body: languageCode, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateExternalLanguageCode(questionnaireId: QuestionnaireId, languageCode: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/external_language_code`,
    {body: languageCode, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateCounterparty(questionnaireId: QuestionnaireId, counterpartyId: CounterpartyId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/counterparty`,
    {body: counterpartyId, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateScopeMatrix(questionnaireId: QuestionnaireId, scopeMatrix: ScopeMatrix) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/scope_matrix`,
    {body: scopeMatrix, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateDeal(questionnaireId: QuestionnaireId, dealId: DealId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/deal`,
    {body: dealId, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function updateArchived(questionnaireId: QuestionnaireId, archived: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/archived`,
    {body: archived, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
    q.vendorToolkit.gettingStartedChecklist.filter(),
  ]);
  return res;
}

export async function updatePromptModifiers(questionnaireId: QuestionnaireId, promptModifiers: PromptModifiers) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/prompt_modifiers`,
    {body: promptModifiers, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
    q.vendorToolkit.gettingStartedChecklist.filter(),
  ]);
  return res;
}

export async function updateTrustCenterVisibility(questionnaireId: QuestionnaireId, visibility: TrustCenterAccess) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/trust_center_visibility`,
    {body: visibility, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export const importQuestions = async (questionnaireId: QuestionnaireId, import_: ImportQuestions) => {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/import`,
    {body: import_, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
    q.vendorToolkit.tasks.filter(),
    q.vendorToolkit.questionnaireAutoComplete.filter(questionnaireId),
  ]);
  return res;
};

export const createDocumentation = async (
  questionnaireId: QuestionnaireId,
  documentation: CreateQuestionnaireDocumentation[],
) => {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/documentation`,
    {body: documentation, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionDocumentation.filter(),
    q.vendorToolkit.question.filter(),
    q.vendorToolkit.questionnaireDocumentation.filter(questionnaireId),
    q.vendorToolkit.questionnaire.filter(questionnaireId),
  ]);
  return res;
};

export const listDocumentation = async (questionnaireId: QuestionnaireId, layerType: LayerType) => {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/questionnaire_layer/{layer_type}/documentation`,
    {path: {questionnaire_id: questionnaireId, layer_type: layerType}},
  );
};

export const deleteDocumentation = async (
  questionnaireId: QuestionnaireId,
  layer_type: LayerType,
  documentationId: DocumentationId,
) => {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/questionnaire_layer/{layer_type}/documentation/{documentation_id}`,
    {path: {questionnaire_id: questionnaireId, layer_type, documentation_id: documentationId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionDocumentation.filter(),
    q.vendorToolkit.question.filter(),
    q.vendorToolkit.questionnaireDocumentation.filter(questionnaireId),
    q.vendorToolkit.questionnaire.filter(questionnaireId),
  ]);
  return res;
};

export const updateDocumentationLinkedToQuestionnaire = async (
  questionnaireId: QuestionnaireId,
  documentationId: DocumentationId,
  linkedToQuestionnaire: boolean,
) => {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/documentation/{documentation_id}/linked_to_questionnaire`,
    {body: linkedToQuestionnaire, path: {questionnaire_id: questionnaireId, documentation_id: documentationId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionDocumentation.filter(),
    q.vendorToolkit.questionnaireDocumentation.filter(questionnaireId),
  ]);
  return res;
};

export const downloadSupportingDocumentation = async (questionnaireId: QuestionnaireId) => {
  const a = document.createElement("a");
  a.href = platformedClient.internalUrl(
    "get",
    "/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/download_documentation",
    {path: {questionnaire_id: questionnaireId}},
  );
  a.click();
};

export const downloadCsv = async (questionnaireId: QuestionnaireId, layer_type: LayerType) => {
  const a = document.createElement("a");
  a.href = platformedClient.internalUrl(
    "get",
    "/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/questionnaire_layer/{layer_type}/download_csv",
    {path: {questionnaire_id: questionnaireId, layer_type}},
  );
  a.click();
};

export const createOriginalFile = async (questionnaireId: QuestionnaireId, fileId: FileId) => {
  const res = await platformedClient.internalRequest(
    "put",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/original_files/{original_file_id}`,
    {path: {questionnaire_id: questionnaireId, original_file_id: fileId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaireAutoComplete.filter(questionnaireId),
  ]);
  return res;
};

export const deleteOriginalFile = async (questionnaireId: QuestionnaireId, fileId: FileId) => {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/original_files/{original_file_id}`,
    {path: {questionnaire_id: questionnaireId, original_file_id: fileId}},
  );
  await invalidateQueries([q.vendorToolkit.questionnaire.filter(questionnaireId)]);
  return res;
};

export const updateOriginalFileImportedAt = async (
  questionnaireId: QuestionnaireId,
  fileId: FileId,
  importedAt: ApiDateTime | null,
) => {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/original_files/{original_file_id}/imported_at`,
    {body: importedAt, path: {questionnaire_id: questionnaireId, original_file_id: fileId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaireAutoComplete.filter(questionnaireId),
  ]);
  return res;
};

export const createOriginalSource = async (questionnaireId: QuestionnaireId, sourceUrl: string) => {
  const res = await platformedClient.internalRequest(
    "put",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/original_sources/{source_url}`,
    {path: {questionnaire_id: questionnaireId, source_url: sourceUrl}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaireAutoComplete.filter(questionnaireId),
  ]);
  return res;
};

export const deleteOriginalSource = async (questionnaireId: QuestionnaireId, sourceUrl: string) => {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/original_sources/{source_url}`,
    {path: {questionnaire_id: questionnaireId, source_url: sourceUrl}},
  );
  await invalidateQueries([q.vendorToolkit.questionnaire.filter(questionnaireId)]);
  return res;
};

export const updateOriginalSourceImportedAt = async (
  questionnaireId: QuestionnaireId,
  sourceUrl: string,
  importedAt: ApiDateTime | null,
) => {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/original_sources/{source_url}/imported_at`,
    {body: importedAt, path: {questionnaire_id: questionnaireId, source_url: sourceUrl}},
  );
  await invalidateQueries([q.vendorToolkit.questionnaire.filter(questionnaireId)]);
  return res;
};

export async function autoCompleteTasks(questionnaireId: QuestionnaireId) {
  const res = await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/auto_complete`,
    {path: {questionnaire_id: questionnaireId}},
  );
  return res;
}

export async function automaticallyAnswer(questionnaireId: QuestionnaireId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/auto_complete`,
    {path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaires.filter(),
    q.vendorToolkit.questionnaireAutoComplete.filter(questionnaireId),
  ]);
  return res;
}

export async function copyBack(questionnaireId: QuestionnaireId) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/copy_back`,
    {path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}

export async function deleteQuestionnaire(questionnaireId: QuestionnaireId) {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}`,
    {path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaires.filter(),
    q.vendorToolkit.facts.filter(),
    q.vendorToolkit.factCounts.filter(),
    q.vendorToolkit.questionUsedFacts.filter(),
    q.vendorToolkit.questionGeneratedFacts.filter(),
  ]);
  return res;
}

export async function updateSectionTitle(questionnaireId: QuestionnaireId, sectionId: SectionId, title: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/sections/{section_id}/title`,
    {body: title, path: {questionnaire_id: questionnaireId, section_id: sectionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
    q.vendorToolkit.question.filter(),
  ]);
  return res;
}

export async function setQAMode(questionnaireId: QuestionnaireId, qaMode: QAMode) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questionnaires/{questionnaire_id}/qa_mode`,
    {body: qaMode, path: {questionnaire_id: questionnaireId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaire.filter(questionnaireId),
    q.vendorToolkit.questionnaires.filter(),
  ]);
  return res;
}
